import { useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal, ModalProps } from 'features/modals';
import { useStageEntryJourneyPrompt } from 'integrations/journeys/job-triggers/use-stage-entry-journey';
import { useStore } from 'models/Provider';
import { IApplication } from 'models/types';

type Props = ModalProps<{
  applicants: IApplication[];
}>;

export const BulkHireApplicantsModal = observer((props: Props) => {
  const { t } = useStore();
  const { applicants, closeModal } = props;
  const [submitting, setSubmitting] = useState(false);
  const { promptOutcomeJourney } = useStageEntryJourneyPrompt(applicants[0]?.job.id ?? 0);

  async function onSubmit() {
    const job = applicants[0].job;
    setSubmitting(true);

    const success = await job.bulkHireApplicants(applicants);

    if (success) {
      closeModal();
      promptOutcomeJourney(
        applicants.map((applicant) => applicant.id),
        'hired'
      );
    } else {
      setSubmitting(false);
    }
  }

  const disableButtons = submitting;
  return (
    <Modal>
      <Modal.Header text={t('job.bulkhire.title')} />
      {submitting && <Loading />}
      {applicants.length === 0 && (
        <>
          <Text text={t('job.bulkhire.noapplicants')} />
          <Modal.Actions>
            <Button
              variant="secondary"
              onClick={closeModal}
              text={t('job.bulkhire.button.close')}
            />
          </Modal.Actions>
        </>
      )}
      {applicants.length > 0 && (
        <>
          <Stack space={3}>
            <Text text={t('job.bulkhire.confirm')} />
            <ShowMore
              max={applicants.length}
              count={applicants.length > 15 ? 10 : applicants.length}
              inc={1000}
              render={({ count }: { count: number }) => (
                <div>
                  {applicants.slice(0, count).map((a, i) => (
                    <Text key={i} font="bold16" color="purple100" text={a.name} />
                  ))}
                </div>
              )}
            />
          </Stack>
          <Modal.Actions>
            <Button
              isDisabled={submitting}
              variant="secondary"
              onClick={closeModal}
              text={t('job.bulkhire.button.cancel')}
            />
            <Button
              isDisabled={disableButtons}
              onClick={onSubmit}
              text={t('job.bulkhire.button.submit')}
            />
          </Modal.Actions>
        </>
      )}
    </Modal>
  );
});
